import { Box, CardActions, CardContent, Typography } from "@mui/material";
import React from "react";
import { EnquiryLabels } from "../../enquiries/enquiries_list";
import ArrowRight from "../../../../assets/arrow_right.svg";
import { useNavigate } from "react-router-dom";
import { EnquiredSchemes } from "../../models/family_enquiry_list";
import dayjs from "dayjs";
import { useFilterEnquiriesStore } from "../../../../store/use-scheme-enquiries-filter-store";
import { purple, red } from "../../../../theme/colors";
import {
  TableCell,
  TableEmptyState,
  TableFooterPagination,
  TableHeaderRow,
  TableRow,
} from "../../../users/components/table_toolkit/table_toolkit";

interface DataGridInterface {
  labels: EnquiryLabels[];
  data: EnquiredSchemes[];
  count: number;
  page: number;
  setPage: (val: number) => void;
}

export default function DataGridEnquiries({
  labels,
  data,
  page,
  setPage,
  count,
}: DataGridInterface) {
  const navigate = useNavigate();
  const { setApplyFilter } = useFilterEnquiriesStore();

  const startRange = (page - 1) * 8 + 1;
  const endRange = Math.min(page * 8, count);
  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage + 1);
    setApplyFilter(true);
  };

  const customText = `Showing ${count ? startRange : 0} to ${endRange || 0} of ${count || 0} entries`;

  function notificationStatusColor(status?: string) {
    if (status === "Success") {
      return "#1E6446";
    } else if (status === "Failure") {
      return red;
    } else {
      return purple;
    }
  }

  return (
    <Box display="flex" flex={1} flexDirection="column" height="100%" width="100%">
      <CardContent sx={{ display: "flex", flexDirection: "column", flexGrow: 1, p: { xs: 2.5, sm: 3 } }}>
        {data.length === 0 ? (
          <TableEmptyState
            message="No enquiries found"
            hint="Try adjusting your search, or filters."
          />
        ) : (
          <Box mt={1}>
            <TableHeaderRow labels={labels.map((l) => ({ label: l.label }))} align="left" bordered />
            {data.map((item) => {
              const originalDate = dayjs(item?.enquiredOn);
              const formattedDate = originalDate?.format?.("DD MMM YYYY");
              return (
                <TableRow
                  key={item?.enquiryId}
                  onClick={() =>
                    navigate(`/schemes/enquiries/${item?.enquiryId}`, {
                      state: item,
                    })
                  }
                  bordered
                >
                  <TableCell label="Ref.No" bordered>
                    <Typography fontSize={14} noWrap>{item?.enquiryId}</Typography>
                  </TableCell>
                  <TableCell label="Full Name" bordered>
                    <Typography fontSize={14} noWrap>{item.name}</Typography>
                  </TableCell>
                  <TableCell label="Contact Number" bordered>
                    <Typography fontSize={14} noWrap>{item.mobileNumber}</Typography>
                  </TableCell>
                  <TableCell label="Scheme Enquired" bordered>
                    <Typography fontSize={14} noWrap>{item.schemeName}</Typography>
                  </TableCell>
                  <TableCell label="State" bordered>
                    <Typography fontSize={14} noWrap>{item.state}</Typography>
                  </TableCell>
                  <TableCell label="District" bordered>
                    <Typography fontSize={14} noWrap>{item.district}</Typography>
                  </TableCell>
                  <TableCell label="Enquired On" bordered>
                    <Typography fontSize={14} noWrap>{formattedDate}</Typography>
                  </TableCell>
                  <TableCell label="Notification Status" isLast bordered>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <Typography
                        fontSize={14}
                        fontWeight={600}
                        noWrap
                        color={notificationStatusColor(item.notificationStatus)}
                      >
                        {item.notificationStatus || "-"}
                      </Typography>
                      <Box sx={{ display: { xs: "none", sm: "block" } }}>
                        <img src={ArrowRight} alt="" height={15} width={15} />
                      </Box>
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </Box>
        )}
      </CardContent>
      {data.length > 0 && (
        <CardActions sx={{ px: 4, pb: 3, mt: "auto" }}>
          <Box width="100%">
            <TableFooterPagination
              count={count}
              page={page - 1}
              onPageChange={handleChangePage}
              summaryText={customText}
              rowsPerPage={8}
            />
          </Box>
        </CardActions>
      )}
    </Box>
  );
}
